import { activeRegionAtom } from "@/atoms/activeRegion";
import { useRegionReports } from "@/lib/reports";
import {
  WAVE_SIZES,
  CONDITIONS,
  CROWD_LEVELS,
  WaveSize,
  Condition,
  CrowdLevel,
  SurfReport,
} from "@/types/surfReport";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { Box, Button, Typography } from "@mui/material";
import { useRecoilValue } from "recoil";
import { useNavigate } from "react-router-dom";
import MiniReportCard from "./MiniReportCard";

const MAX_PREVIEW = 3;

interface ReportsSectionProps {
  onReport: () => void;
  /** Fires when an unauthenticated user taps an interactive control
   *  (e.g. confirm/deny a report). Parent typically opens a sign-in prompt. */
  onAuthRequired: () => void;
  /** Expand the empty-state when this is the primary interaction
   *  surface for the region (e.g. no webcam available). */
  prominent?: boolean;
  cameraName?: string;
}

function mode<T extends string>(reports: SurfReport[], key: keyof SurfReport): T | null {
  if (reports.length === 0) return null;
  const counts: Record<string, number> = {};
  for (const r of reports) {
    const val = r[key] as string;
    counts[val] = (counts[val] || 0) + 1;
  }
  let best = "";
  let bestCount = 0;
  for (const [val, count] of Object.entries(counts)) {
    if (count > bestCount) {
      best = val;
      bestCount = count;
    }
  }
  return best as T;
}

export default function ReportsSection({
  onReport,
  onAuthRequired,
  prominent = false,
  cameraName,
}: ReportsSectionProps) {
  const activeRegion = useRecoilValue(activeRegionAtom);
  const { data: reports = [], isLoading: reportsLoading } =
    useRegionReports(activeRegion);
  const navigate = useNavigate();

  const topWave = mode<WaveSize>(reports, "waveSize");
  const topCondition = mode<Condition>(reports, "conditions");
  const topCrowd = mode<CrowdLevel>(reports, "crowd");

  const previewReports = reports.slice(0, MAX_PREVIEW);
  const hasReports = reports.length > 0;
  const hasMore = reports.length > previewReports.length;
  // Consensus is only meaningful with ≥2 reports. With a single report
  // the card below is the whole story — showing the same values in the
  // strip duplicates info and weakens the "Add report" CTA.
  const showConsensus = reports.length >= 2;

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      {/* Pulse header: count · consensus tags · Add report */}
      <Box
        sx={{
          py: 1.5,
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: { xs: 1.25, md: 2 },
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          <Typography variant="overline" sx={{ color: "text.secondary", flexShrink: 0 }}>
            {hasReports
              ? `${reports.length} report${reports.length === 1 ? "" : "s"}`
              : "Reports"}
          </Typography>

          {showConsensus && topWave ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: "text.secondary",
                  flexShrink: 0,
                  letterSpacing: "0.08em",
                }}
              >
                Consensus
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.875rem", md: "0.9375rem" },
                  color: "text.primary",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {WAVE_SIZES[topWave].label}
              </Typography>
              {topCondition && (
                <ConsensusTag
                  color={CONDITIONS[topCondition].color}
                  label={CONDITIONS[topCondition].label}
                />
              )}
              {topCrowd && (
                <ConsensusTag
                  color={CROWD_LEVELS[topCrowd].color}
                  label={CROWD_LEVELS[topCrowd].label}
                />
              )}
            </Box>
          ) : !hasReports && !prominent && !reportsLoading ? (
            <Typography
              sx={{
                fontSize: { xs: "0.875rem", md: "0.9375rem" },
                color: "text.primary",
                lineHeight: 1.35,
              }}
            >
              No reports yet — be the first to call it.
            </Typography>
          ) : null}
        </Box>

        <AddReportLink onClick={onReport} label={ctaLabel(reports.length)} />
      </Box>

      {/* Body: cards grid, or expanded empty state */}
      {hasReports ? (
        <Box
          sx={{
            mt: 1.5,
            display: "grid",
            gap: 1.25,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
          }}
        >
          {previewReports.map((r) => (
            <MiniReportCard
              key={r.id}
              report={r}
              onClick={() => navigate("/feed")}
              onAuthRequired={onAuthRequired}
            />
          ))}
        </Box>
      ) : prominent && !reportsLoading ? (
        <Box sx={{ py: { xs: 4, sm: 6 }, px: 2, textAlign: "center" }}>
          <Typography variant="h3" sx={{ color: "text.primary", mb: 1 }}>
            Be the first to call it.
          </Typography>
          <Typography
            sx={{
              fontSize: "0.95rem",
              color: "text.secondary",
              maxWidth: 420,
              mx: "auto",
              mb: 2.5,
              lineHeight: 1.5,
            }}
          >
            {cameraName
              ? `No reports yet for ${cameraName}. Tell the community how it looks right now.`
              : "No cameras or reports here yet. Share what you're seeing and help the next surfer check conditions."}
          </Typography>
          <Button
            variant="contained"
            startIcon={<EditNoteIcon />}
            onClick={onReport}
            sx={{ textTransform: "none", fontWeight: 600, px: 3, py: 1 }}
          >
            Report Conditions
          </Button>
        </Box>
      ) : null}

      {hasReports && (
        <Box sx={{ textAlign: "center", mt: 1 }}>
          <Button
            size="small"
            onClick={() => navigate("/feed")}
            sx={{
              fontSize: "0.75rem",
              color: "text.secondary",
              "&:hover": { color: "accent.main" },
            }}
          >
            {hasMore ? `View all ${reports.length} reports →` : "View full feed →"}
          </Button>
        </Box>
      )}
    </Box>
  );
}

function ConsensusTag({ color, label }: { color: string; label: string }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: { xs: "0.8125rem", md: "0.875rem" },
          color: "text.primary",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function ctaLabel(count: number): string {
  if (count === 0) return "Be the first";
  if (count === 1) return "Add your take";
  return "Add report";
}

function AddReportLink({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{
        all: "unset",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        color: "accent.main",
        fontSize: "0.875rem",
        "&:hover": { textDecoration: "underline", textUnderlineOffset: "3px" },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "accent.main",
          outlineOffset: 2,
        },
      }}
    >
      {label}
      <ArrowForwardIcon sx={{ fontSize: 16 }} />
    </Box>
  );
}
